/**
 * Phân trang danh sách BĐS — điều hướng trang đầu/cuối, trước/sau và số trang.
 *
 * Ẩn hoàn toàn khi chỉ có một trang hoặc không có dữ liệu pagination.
 */
import { TEXT } from '../../shared/languages';

const ChevronLeft = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M10 3L5 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M6 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronFirst = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M11 3L6 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 3L2 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLast = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Tạo mảng số trang và ellipsis cho thanh phân trang. */
const buildPageItems = (currentPage, lastPage) => {
  if (lastPage <= 1) {
    return [];
  }

  if (lastPage <= 7) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  const items = [1];

  if (currentPage <= 4) {
    for (let page = 2; page <= 4; page += 1) {
      items.push(page);
    }

    items.push('ellipsis-end');
    items.push(lastPage);
    return items;
  }

  if (currentPage >= lastPage - 3) {
    items.push('ellipsis-start');

    for (let page = lastPage - 3; page <= lastPage - 1; page += 1) {
      items.push(page);
    }

    items.push(lastPage);
    return items;
  }

  items.push('ellipsis-start');

  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    items.push(page);
  }

  items.push('ellipsis-end');
  items.push(lastPage);

  return items;
};

/**
 * Thanh phân trang.
 * @param {Object} props
 * @param {Object} props.pagination - Meta từ API (current_page, last_page)
 * @param {boolean} props.isLoading
 * @param {Function} props.onPageChange
 */
export const Pagination = ({ pagination, isLoading, onPageChange }) => {
  if (!pagination || pagination.last_page <= 1) {
    return null;
  }

  const currentPage = pagination.current_page;
  const lastPage = pagination.last_page;
  const pageItems = buildPageItems(currentPage, lastPage);
  const common = TEXT.common;

  return (
    <nav className="pagination" aria-label="Property pagination">
      <div className="pagination-group">
        <button
          className="pagination-control pagination-control--first"
          disabled={isLoading || currentPage === 1}
          onClick={() => onPageChange(1)}
          type="button"
          aria-label={common.firstPage}
        >
          <ChevronFirst />
          <span>{common.firstPage}</span>
        </button>

        <button
          className="pagination-control pagination-control--prev"
          disabled={isLoading || currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          type="button"
          aria-label={common.previous}
        >
          <ChevronLeft />
          <span>{common.previous}</span>
        </button>
      </div>

      <div className="pagination-pages">
        {pageItems.map((item) => {
          if (typeof item === 'string') {
            return (
              <span key={item} className="pagination-ellipsis" aria-hidden="true">
                …
              </span>
            );
          }

          const isActive = item === currentPage;

          return (
            <button
              key={item}
              className={`pagination-page ${isActive ? 'is-active' : ''}`}
              disabled={isLoading}
              onClick={() => onPageChange(item)}
              type="button"
              aria-label={`Trang ${item}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {item}
            </button>
          );
        })}
      </div>

      <div className="pagination-group">
        <button
          className="pagination-control pagination-control--next"
          disabled={isLoading || currentPage === lastPage}
          onClick={() => onPageChange(currentPage + 1)}
          type="button"
          aria-label={common.next}
        >
          <span>{common.next}</span>
          <ChevronRight />
        </button>

        <button
          className="pagination-control pagination-control--last"
          disabled={isLoading || currentPage === lastPage}
          onClick={() => onPageChange(lastPage)}
          type="button"
          aria-label={common.lastPage}
        >
          <span>{common.lastPage}</span>
          <ChevronLast />
        </button>
      </div>
    </nav>
  );
};
